import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { FileSearch } from 'lucide-react';
import PendingRequestDetails from '../components/PendingRequestDetails';
import { compareDateValues, formatDateTimesInText, formatHumanDateTime } from '../helpers/dateTime';

function LocationRequests({
  accessToken,
  locationRequests = [],
  locationRequestsLoading = false,
  locationRequestsError = '',
  refreshLocationRequests = async () => {},
  onCancelRequest,
  cancellingRequests = false,
}) {
  const theme = useTheme();
  const isMobileViewport = useMediaQuery(theme.breakpoints.down('sm'));
  const [detailsSelection, setDetailsSelection] = useState(null);
  const selectedRequest = useMemo(() => locationRequests.find((request) => (
    request.sheetName === detailsSelection?.sheetName
    && Number(request.sheetRowNumber) === Number(detailsSelection?.sheetRowNumber)
    && request.usuarioEmail === detailsSelection?.usuarioEmail
    && request.iglesiaEstado === detailsSelection?.iglesiaEstado
    && request.iglesiaCiudad === detailsSelection?.iglesiaCiudad
    && request.iglesiaNombre === detailsSelection?.iglesiaNombre
    && request.pais === detailsSelection?.pais
  )) || null, [detailsSelection, locationRequests]);

  useEffect(() => {
    setDetailsSelection(null);
  }, [accessToken]);

  useEffect(() => {
    if (detailsSelection && !selectedRequest) setDetailsSelection(null);
  }, [detailsSelection, selectedRequest]);

  const closeDetails = useCallback(() => setDetailsSelection(null), []);
  const canCancelRequests = typeof onCancelRequest === 'function';

  const rows = useMemo(
    () => locationRequests.map((request, index) => ({ id: `${request.sheetName}-${request.sheetRowNumber}-${index}`, ...request })),
    [locationRequests]
  );

  const columns = useMemo(
    () => [
      {
        field: 'actions',
        headerName: 'Acciones',
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        minWidth: canCancelRequests ? (isMobileViewport ? 152 : 300) : (isMobileViewport ? 56 : 145),
        renderCell: (params) => (
          <Stack direction="row" spacing={1} alignItems="center" sx={{ height: '100%' }}>
            {isMobileViewport ? (
              <Tooltip title="Ver cambios">
                <IconButton
                  type="button"
                  size="small"
                  color="primary"
                  aria-label={`Ver cambios de ${params.row.usuarioEmail || 'este usuario'}`}
                  onClick={() => setDetailsSelection(params.row)}
                >
                  <FileSearch size={18} />
                </IconButton>
              </Tooltip>
            ) : (
              <Button
                type="button"
                size="small"
                variant="outlined"
                startIcon={<FileSearch size={16} />}
                aria-label={`Ver cambios de ${params.row.usuarioEmail || 'este usuario'}`}
                onClick={() => setDetailsSelection(params.row)}
                sx={{ whiteSpace: 'nowrap' }}
              >
                Ver cambios
              </Button>
            )}
            {canCancelRequests && <Button
              type="button"
              color="error"
              size="small"
              variant="outlined"
              onClick={() => onCancelRequest(params.row)}
              disabled={locationRequestsLoading || cancellingRequests}
              sx={{ whiteSpace: 'nowrap' }}
            >
              {isMobileViewport ? 'Cancelar' : 'Cancelar cambios'}
            </Button>}
          </Stack>
        ),
      },
      { field: 'type', headerName: 'Tipo', minWidth: 170, flex: 1 },
      {
        field: 'solicitadoPorEmail',
        headerName: 'Solicitado por (Email)',
        minWidth: 220,
        flex: 1,
      },
      {
        field: 'usuarioEmail',
        headerName: 'Usuario (Email)',
        minWidth: 220,
        flex: 1,
      },
      {
        field: 'usuarioNombre',
        headerName: 'Usuario (Nombre)',
        minWidth: 180,
        flex: 1,
      },
      {
        field: 'usuarioApellido',
        headerName: 'Usuario (Apellido)',
        minWidth: 180,
        flex: 1,
      },
      { field: 'detalle', headerName: 'Detalle', minWidth: 260, flex: 1.5 },
      {
        field: 'fecha',
        headerName: 'Fecha',
        minWidth: 180,
        flex: 1,
        sortComparator: (leftValue, rightValue) => compareDateValues(leftValue, rightValue),
        renderCell: (params) => formatHumanDateTime(params.row?.fecha, params.row?.fecha || ''),
      },
    ],
    [canCancelRequests, cancellingRequests, isMobileViewport, locationRequestsLoading, onCancelRequest]
  );

  return (
    <Box component="main" sx={{ width: '100%', p: { xs: 1.5, md: 2 } }}>
      <Paper variant="outlined" sx={{ p: 1.5 }}>
        <Stack spacing={1.5}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1}
            alignItems={{ xs: 'stretch', sm: 'center' }}
            justifyContent="space-between"
          >
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
              <Typography variant="h6">Solicitudes en tu sede</Typography>
              <Chip label="Pendientes" color="secondary" size="small" />
              <Chip label={locationRequests.length} color="error" size="small" />
            </Stack>
            <Button
              type="button"
              variant="contained"
              onClick={() => refreshLocationRequests({ force: true })}
              disabled={locationRequestsLoading}
              sx={{ alignSelf: { xs: 'stretch', sm: 'auto' } }}
            >
              {locationRequestsLoading ? 'Cargando...' : 'Actualizar'}
            </Button>
          </Stack>

          {locationRequestsError && <Alert severity="error">{formatDateTimesInText(locationRequestsError)}</Alert>}
          <Box sx={{ borderTop: 1, borderColor: 'divider', height: { xs: '62vh', md: '70vh' }, pt: 1.5 }}>
            <DataGrid
              rows={rows}
              columns={columns}
              loading={locationRequestsLoading}
              pageSizeOptions={isMobileViewport ? [5, 10, 25, 100] : [10, 25, 50, 100]}
              initialState={{ pagination: { paginationModel: { pageSize: 100, page: 0 } } }}
              columnVisibilityModel={isMobileViewport
                ? {
                  solicitadoPorEmail: false,
                  usuarioApellido: false,
                }
                : undefined}
              density={isMobileViewport ? 'compact' : 'standard'}
              disableRowSelectionOnClick
              sx={{
                '& .MuiDataGrid-menuIconButton': {
                  opacity: 1,
                  visibility: 'visible',
                },
                '& .MuiDataGrid-menuIcon': {
                  visibility: 'visible',
                  width: 'auto',
                },
                '& .MuiDataGrid-menuIcon .MuiIconButton-root': {
                  opacity: 1,
                  pointerEvents: 'auto',
                },
              }}
            />
          </Box>
        </Stack>
      </Paper>

      <Dialog open={Boolean(selectedRequest)} onClose={closeDetails} maxWidth="md" fullWidth>
        <DialogTitle>Detalle de la solicitud</DialogTitle>
        <DialogContent dividers>
          {selectedRequest && (
            <PendingRequestDetails
              request={selectedRequest}
              onCancelRequest={onCancelRequest}
              cancelling={cancellingRequests}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button type="button" onClick={closeDetails}>Cerrar</Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
}

export default LocationRequests;
